/**
 * Rebuild attached docs, ticks, uploads, and mission variable values from
 * clipboard JSON. Doc ids are workspace-global, so they are copied by
 * reference after a shape check.
 */

export function cloneAttachedDocsFromClipboard(clipboardTask) {
  const entries = (Array.isArray(clipboardTask.attachedDocs) ? clipboardTask.attachedDocs : [])
    .filter(e => e && typeof e.docId === 'string' && e.docId)
    .map(e => ({ docId: e.docId, title: e.title, icon: e.icon }));
  if (entries.length === 0) return { attachedDocs: undefined, docTicks: undefined, docFiles: undefined };

  const attachedIds = new Set(entries.map(e => e.docId));

  let docTicks;
  if (clipboardTask.docTicks && typeof clipboardTask.docTicks === 'object' && !Array.isArray(clipboardTask.docTicks)) {
    docTicks = {};
    for (const [docId, overrides] of Object.entries(clipboardTask.docTicks)) {
      if (!attachedIds.has(docId)) continue;
      if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) continue;
      docTicks[docId] = { ...overrides };
    }
    if (Object.keys(docTicks).length === 0) docTicks = undefined;
  }

  let docFiles;
  if (clipboardTask.docFiles && typeof clipboardTask.docFiles === 'object' && !Array.isArray(clipboardTask.docFiles)) {
    docFiles = {};
    for (const [docId, overrides] of Object.entries(clipboardTask.docFiles)) {
      if (!attachedIds.has(docId)) continue;
      if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) continue;
      const next = {};
      for (const [blockId, list] of Object.entries(overrides)) {
        if (!Array.isArray(list)) continue;
        const files = list.filter((f) => f && typeof f.attachmentId === 'string' && f.attachmentId);
        if (files.length) next[blockId] = files.map((f) => ({ ...f }));
      }
      if (Object.keys(next).length) docFiles[docId] = next;
    }
    if (Object.keys(docFiles).length === 0) docFiles = undefined;
  }

  return { attachedDocs: entries, docTicks, docFiles };
}

/** Mission variable overlays are a flat list, copied as a snapshot. */
export function cloneMissionVariableValues(clipboardTask) {
  if (!Array.isArray(clipboardTask?.variableValues)) return undefined;
  const rows = clipboardTask.variableValues
    .filter((row) => row && typeof row.name === 'string' && row.name)
    .map((row) => ({
      ...row,
      values: Array.isArray(row.values) ? [...row.values] : row.values,
    }));
  return rows.length ? rows : undefined;
}
